import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  useTheme,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Chat as MessagesIcon,
  School as SchoolIcon,
  MedicalServices as HealthIcon,
  SportsBasketball as ActivitiesIcon,
  Contacts as ContactsIcon,
  AccountBalance as MoneyIcon,
  EmojiEvents as DisciplineIcon,
  Note as TasksIcon,
  PhotoAlbum as MemoriesIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner, MemberAvatar } from '../../components/common';
import { useFamily } from '../../context';
import { colors } from '../../theme';

interface StatItem {
  label: string;
  value: string | number;
}

interface FeatureCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  stats?: StatItem[];
  onClick: () => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, icon, color, stats, onClick }) => {
  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        height: '100%',
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: `${color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {React.isValidElement(icon) && React.cloneElement(icon, {
              sx: { fontSize: 28, color },
            } as any)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {title}
            </Typography>
            {stats && stats.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                {stats.map((stat, index) => (
                  <React.Fragment key={index}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', lineHeight: 1.5 }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {stat.value}
                      </Typography>
                      {' '}{stat.label}
                    </Typography>
                    {index < stats.length - 1 && (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>·</Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { memberList, currentFamily, stats, isLoading } = useFamily();

  if (isLoading) {
    return <LoadingSpinner message="Loading your family data..." />;
  }

  const features = [
    {
      title: 'Calendar',
      icon: <CalendarIcon />,
      color: colors.calendar,
      path: '/calendar',
      stats: [{ label: 'events today', value: stats?.events || 0 }],
    },
    {
      title: 'Messages',
      icon: <MessagesIcon />,
      color: colors.messages,
      path: '/messages',
      stats: [{ label: 'new messages', value: stats?.messages || 0 }],
    },
    {
      title: 'Notebook',
      icon: <TasksIcon />,
      color: colors.notes,
      path: '/notebook',
      stats: [
        { label: 'notes', value: stats?.notes || 0 },
        { label: 'lists', value: stats?.lists || 0 },
      ],
    },
    {
      title: 'Money',
      icon: <MoneyIcon />,
      color: colors.money,
      path: '/money',
      stats: [
        { label: 'owed by me', value: formatCurrency(stats?.tally?.owedByMe || 0) },
        { label: 'owed to me', value: formatCurrency(stats?.tally?.owedToMe || 0) },
      ],
    },
    {
      title: 'Scrapbook',
      icon: <MemoriesIcon />,
      color: colors.memories,
      path: '/memories',
      stats: [
        { label: 'photos', value: stats?.photos || 0 },
        { label: 'videos', value: stats?.videos || 0 },
        { label: 'quotes', value: stats?.quotes || 0 },
      ],
    },
    {
      title: 'Contacts',
      icon: <ContactsIcon />,
      color: colors.contacts,
      path: '/contacts',
      stats: [{ label: 'contacts', value: stats?.contacts || 0 }],
    },
    {
      title: 'Activities',
      icon: <ActivitiesIcon />,
      color: colors.activities,
      path: '/activities',
      stats: [{ label: 'activities', value: stats?.activities || 0 }],
    },
    {
      title: 'Health',
      icon: <HealthIcon />,
      color: colors.health,
      path: '/health',
      stats: [
        { label: 'illnesses', value: stats?.illnesses || 0 },
        { label: 'allergies', value: stats?.allergies || 0 },
        { label: 'medicines', value: stats?.doses || 0 },
      ],
    },
    {
      title: 'School',
      icon: <SchoolIcon />,
      color: colors.school,
      path: '/school',
      stats: [
        { label: 'classes', value: stats?.classes || 0 },
        { label: 'assignments', value: stats?.assignments || 0 },
      ],
    },
    {
      title: 'Discipline',
      icon: <DisciplineIcon />,
      color: colors.discipline,
      path: '/discipline',
      stats: [
        { label: 'chores', value: stats?.chores || 0 },
        { label: 'routines', value: stats?.routines || 0 },
      ],
    },
  ];

  return (
    <Box>
      <PageHeader
        title={`Welcome${currentFamily?.data?.name ? `, ${currentFamily.data.name}` : ''}`}
        subtitle="Here's what's happening with your family"
      />

      {/* Family Members Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            My Family
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2 }}>
            {memberList.map((member) => {
              const profile = member.data?.profile;
              const memberData = currentFamily?.data?.members?.[member.id];
              const initials = profile?.first
                ? `${profile.first[0]}${profile.last ? profile.last[0] : ''}`
                : '?';

              return (
                <Box
                  key={member.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      '& .MuiAvatar-root': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                  onClick={() => navigate(`/settings/member/${member.id}`)}
                >
                  <MemberAvatar
                    profilePhotoId={profile?.profilePhotoId}
                    name={`${profile?.first || ''} ${profile?.last || ''}`.trim()}
                    color={memberData?.color || theme.palette.primary.main}
                    sx={{
                      width: 64,
                      height: 64,
                      fontSize: '1.25rem',
                      mb: 1,
                      transition: 'transform 0.2s',
                    }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {profile?.first || 'Unknown'}
                  </Typography>
                  {memberData?.role && (
                    <Chip
                      label={memberData.role}
                      size="small"
                      sx={{
                        mt: 0.5,
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: `${memberData.color}30`,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Today Section Header */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Today
      </Typography>

      {/* Feature Grid - matching mobile layout */}
      <Grid container spacing={2}>
        {features.map((feature) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={feature.path}>
            <FeatureCard
              title={feature.title}
              icon={feature.icon}
              color={feature.color}
              stats={feature.stats}
              onClick={() => navigate(feature.path)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HomePage;
